/**
 * InfinitSales - Channel Selection Step
 * Onboarding step for selecting WhatsApp connection type
 * 
 * Design: Premium cards with hover effects, following platform theme
 */
import React, { useState } from 'react';
import { 
  MessageCircle, 
  Building2, 
  Shield, 
  Zap, 
  CheckCircle2,
  ArrowRight,
  Loader2,
  Sparkles
} from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';

// WhatsApp Logo Component (SVG)
const WhatsAppLogo = ({ size = 32, color = "#25D366" }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 24 24" 
    fill={color}
  >
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

// WhatsApp Business Logo Component (SVG)  
const WhatsAppBusinessLogo = ({ size = 32 }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 24 24" 
    fill="none"
  >
    <defs>
      <linearGradient id="wa-business-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#25D366" />
        <stop offset="100%" stopColor="#128C7E" />
      </linearGradient>
    </defs>
    <path 
      d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"
      fill="url(#wa-business-gradient)"
    />
    {/* Business badge */}
    <circle cx="18" cy="18" r="5" fill="#128C7E" stroke="white" strokeWidth="1.5"/>
    <path d="M16 18h4M18 16v4" stroke="white" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

const ChannelSelectionStep = ({ onSelect, loading = false }) => {
  const { colors, theme } = useTheme();
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [hoveredCard, setHoveredCard] = useState(null);

  const handleSelect = (channel) => {
    setSelectedChannel(channel);
  };

  const handleContinue = () => {
    if (selectedChannel && onSelect) {
      onSelect(selectedChannel);
    }
  };

  const channels = [
    {
      id: 'wa_personal',
      name: 'WhatsApp Pessoal',
      description: 'Conecte seu número pessoal ou comercial via QR Code',
      icon: WhatsAppLogo,
      features: [
        { icon: Zap, text: 'Configuração em 2 minutos' },
        { icon: MessageCircle, text: 'Use seu número atual' },
      ],
      badge: null,
      accentColor: '#25D366',
    },
    {
      id: 'wa_business',
      name: 'WhatsApp Business',
      description: 'API oficial da Meta com recursos avançados',
      icon: WhatsAppBusinessLogo,
      features: [
        { icon: Shield, text: 'Sem risco de bloqueio' },
        { icon: Building2, text: 'API oficial Meta' },
      ],
      badge: 'Recomendado',
      accentColor: '#128C7E',
    },
  ];

  const styles = {
    container: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      padding: '32px 24px',
      maxWidth: '800px',
      margin: '0 auto',
    },
    header: {
      textAlign: 'center',
      marginBottom: '40px',
    },
    welcomeBadge: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '6px',
      padding: '6px 14px',
      backgroundColor: `${colors.accentPrimary}15`,
      color: colors.accentPrimary,
      borderRadius: '20px',
      fontSize: '13px',
      fontWeight: 600,
      marginBottom: '16px',
    },
    title: {
      fontSize: '28px',
      fontWeight: 700,
      color: colors.textPrimary,
      marginBottom: '12px',
      lineHeight: 1.3,
    },
    subtitle: {
      fontSize: '16px',
      color: colors.textSecondary,
      lineHeight: 1.5,
      maxWidth: '480px',
    },
    cardsContainer: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
      gap: '20px',
      width: '100%',
      marginBottom: '32px',
    },
    card: {
      position: 'relative',
      padding: '28px',
      backgroundColor: colors.bgPrimary,
      border: `2px solid ${colors.borderColor}`,
      borderRadius: '16px',
      cursor: 'pointer',
      transition: 'all 0.25s ease',
      overflow: 'hidden',
    },
    cardHovered: {
      transform: 'translateY(-4px)',
      boxShadow: `0 12px 40px -12px ${colors.accentPrimary}30`,
    },
    cardSelected: {
      borderColor: colors.accentPrimary,
      backgroundColor: theme === 'dark' 
        ? `${colors.accentPrimary}08` 
        : `${colors.accentPrimary}05`,
    },
    cardBadge: {
      position: 'absolute',
      top: '16px',
      right: '16px',
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
      padding: '4px 10px',
      backgroundColor: colors.accentPrimary,
      color: 'white',
      borderRadius: '6px',
      fontSize: '11px',
      fontWeight: 600,
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
    },
    cardIconContainer: {
      width: '56px',
      height: '56px',
      borderRadius: '14px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginBottom: '20px',
      transition: 'transform 0.2s ease',
    },
    cardName: {
      fontSize: '18px',
      fontWeight: 700,
      color: colors.textPrimary,
      marginBottom: '8px',
    },
    cardDescription: {
      fontSize: '14px',
      color: colors.textSecondary,
      lineHeight: 1.5,
      marginBottom: '20px',
    },
    cardFeatures: {
      display: 'flex',
      flexDirection: 'column',
      gap: '10px',
    },
    cardFeature: {
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      fontSize: '13px',
      color: colors.textSecondary,
    },
    cardFeatureIcon: {
      width: '28px',
      height: '28px',
      borderRadius: '8px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    },
    selectedIndicator: {
      position: 'absolute',
      bottom: '16px',
      right: '16px',
      width: '24px',
      height: '24px',
      borderRadius: '50%',
      backgroundColor: colors.accentPrimary,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: 'white',
      opacity: 0,
      transform: 'scale(0.8)',
      transition: 'all 0.2s ease',
    },
    selectedIndicatorVisible: {
      opacity: 1,
      transform: 'scale(1)',
    },
    continueButton: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
      padding: '16px 32px',
      backgroundColor: selectedChannel ? colors.accentPrimary : colors.borderColor,
      color: selectedChannel ? 'white' : colors.textTertiary,
      border: 'none',
      borderRadius: '12px',
      fontSize: '15px',
      fontWeight: 600,
      cursor: selectedChannel ? 'pointer' : 'not-allowed',
      transition: 'all 0.2s ease',
      minWidth: '200px',
    },
    continueButtonEnabled: {
      boxShadow: `0 4px 14px ${colors.accentPrimary}40`,
    },
    footer: {
      marginTop: '24px',
      textAlign: 'center',
      fontSize: '13px',
      color: colors.textTertiary,
    },
    footerLink: {
      color: colors.accentPrimary,
      cursor: 'pointer',
      textDecoration: 'none',
    },
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.welcomeBadge}>
          <Sparkles size={14} />
          Vamos começar
        </div>
        <h1 style={styles.title}>
          Como você quer conectar<br />seu WhatsApp?
        </h1>
        <p style={styles.subtitle}>
          Escolha o método de conexão. Você poderá adicionar mais canais depois.
        </p>
      </div>

      {/* Channel Cards */}
      <div style={styles.cardsContainer}>
        {channels.map((channel) => {
          const isSelected = selectedChannel === channel.id;
          const isHovered = hoveredCard === channel.id;
          const IconComponent = channel.icon;

          return (
            <div
              key={channel.id}
              style={{
                ...styles.card,
                ...(isHovered ? styles.cardHovered : {}),
                ...(isSelected ? styles.cardSelected : {}),
                borderColor: isSelected ? channel.accentColor : colors.borderColor,
              }}
              onClick={() => handleSelect(channel.id)}
              onMouseEnter={() => setHoveredCard(channel.id)}
              onMouseLeave={() => setHoveredCard(null)}
            >
              {/* Badge */}
              {channel.badge && (
                <div style={{
                  ...styles.cardBadge,
                  backgroundColor: channel.accentColor,
                }}>
                  <Sparkles size={10} />
                  {channel.badge}
                </div>
              )}

              {/* Icon */}
              <div 
                style={{
                  ...styles.cardIconContainer,
                  backgroundColor: `${channel.accentColor}15`,
                  transform: isHovered ? 'scale(1.05)' : 'scale(1)',
                }}
              >
                <IconComponent size={28} color={channel.accentColor} />
              </div>

              {/* Content */}
              <h3 style={styles.cardName}>{channel.name}</h3>
              <p style={styles.cardDescription}>{channel.description}</p>

              {/* Features */}
              <div style={styles.cardFeatures}>
                {channel.features.map((feature, index) => {
                  const FeatureIcon = feature.icon;
                  return (
                    <div key={index} style={styles.cardFeature}>
                      <div style={{
                        ...styles.cardFeatureIcon,
                        backgroundColor: `${channel.accentColor}10`,
                        color: channel.accentColor,
                      }}>
                        <FeatureIcon size={14} />
                      </div>
                      <span>{feature.text}</span>
                    </div>
                  );
                })}
              </div>

              {/* Selected Indicator */}
              <div style={{
                ...styles.selectedIndicator,
                ...(isSelected ? styles.selectedIndicatorVisible : {}),
                backgroundColor: channel.accentColor,
              }}>
                <CheckCircle2 size={14} />
              </div>
            </div>
          );
        })}
      </div>

      {/* Continue Button */}
      <button
        style={{
          ...styles.continueButton,
          ...(selectedChannel ? styles.continueButtonEnabled : {}),
        }}
        onClick={handleContinue}
        disabled={!selectedChannel || loading}
      >
        {loading ? (
          <>
            <Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} />
            Configurando...
          </>
        ) : (
          <>
            Continuar
            <ArrowRight size={18} />
          </>
        )}
      </button>

      {/* Footer */}
      <div style={styles.footer}>
        <p>
          Você poderá conectar outros canais depois nas configurações
        </p>
      </div>

      {/* Keyframes for spinner */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default ChannelSelectionStep;